<template>
  <!-- Temperature page -->
  <div data-page="temperature" class=" ">
    <div class="grid grid-cols-[auto_1fr] divide-y ">
      <div v-for="(d, index) in daily" :key="d.time" class="col-span-2 grid grid-cols-subgrid active:bg-hover transition-colors duration-200"
           @click="toggleDay(index)">
        <!-- Day label -->
        <div class="min-h-16 content-center pr-12 px-4 xl:pr-32 dowHeader">
          {{ formatDow(d.time, timezone, t, dateFormat) }}
        </div>

        <!-- Collapsed temperature range bar -->
        <div class="flex pr-12 transition-opacity duration-300 h-full items-center" :class="{ 'opacity-0': expandedDayIndex === index }">
          <div class="relative h-11 w-full">
            <!-- All elements are vertically centered using a combination of -->
            <!-- absolute, top-1/2, and -translate-y-1/2 for consistency. -->

            <!-- Range line -->
            <!-- Note: Increased thickness with h-1.5 and removed rounded-full. -->
            <div
                class="temp-bar-element-base h-1.5 bg-orange-500"
                :style="{
      left: getTempPosition(d.temperatureMin),
      right: `${100 - parseFloat(getTempPosition(d.temperatureMax))}%`
    }"
            ></div>

            <!-- Min temp vertical bar (replaces the circle) -->
            <!-- Note: Changed shape to a tall, thin bar (h-5 w-1.5). -->
            <div
                class="temp-bar-element-base h-5 w-1.5 -translate-x-1/2 bg-orange-500"
                :style="{ left: getTempPosition(d.temperatureMin) }"
            ></div>

            <!-- Max temp vertical bar (replaces the circle) -->
            <div
                class="temp-bar-element-base h-5 w-1.5 -translate-x-1/2 bg-orange-500"
                :style="{ left: getTempPosition(d.temperatureMax) }"
            ></div>

            <!-- Min temp label -->
            <!-- Note: Positioned to the left of the bar. Color changed to gray. -->
            <div
                class="temp-bar-element-base -translate-x-full pr-2 text-sm text-secondary"
                :style="{ left: getTempPosition(d.temperatureMin) }"
            >
              {{ formatTemp(d.temperatureMin, settingsStore.temperatureUnits) }}
            </div>

            <!-- Max temp label -->
            <!-- Note: Positioned to the right of the bar. Color changed to black. -->
            <div
                class="temp-bar-element-base pl-2 text-sm"
                :style="{ left: getTempPosition(d.temperatureMax) }"
            >
              {{ formatTemp(d.temperatureMax, settingsStore.temperatureUnits) }}
            </div>

          </div>
        </div>

        <!-- Expanded day chart and timeline -->
        <transition
            enter-active-class="transition-all duration-500 ease-in-out"
            leave-active-class="transition-all duration-500 ease-in-out"
            enter-from-class="grid-rows-[0fr] opacity-0"
            enter-to-class="grid-rows-[1fr] opacity-100"
            leave-from-class="grid-rows-[1fr] opacity-100"
            leave-to-class="grid-rows-[0fr] opacity-0"
        >
          <div v-if="expandedDayIndex === index" class="grid col-span-full overflow-hidden">
            <div class="min-h-0">
              <div class="flex flex-col pb-4">
                <canvas
                    class="h-24 w-full overflow-visible"
                    :ref="el => tempChartRefs[index] = el"
                ></canvas>
                <div class="relative h-6 w-full">
                  <WeatherTimeline :d="d" :timezone="timezone"/>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useSettingsStore } from '../stores/settings.js';
import { useI18n } from 'vue-i18n';
import { watch, ref, computed } from 'vue';
import { formatTemp, formatDow } from '../utils/formatters.js';
import WeatherTimeline from './WeatherTimeline.vue';
import { drawTempChart } from '../utils/temperatureChart.js';

const settingsStore = useSettingsStore();

const { t, d: dateFormat } = useI18n();

const props = defineProps({
  daily: Array,
  timezone: String,
  expandedDayIndex: Number,
  toggleDay: Function,
});

const tempChartRefs = ref([]);
const activeCanvas = computed(() => tempChartRefs.value[props.expandedDayIndex]);

const weeklyMin = computed(() => {
  if (!props.daily || props.daily.length === 0) return 0;
  return Math.min(...props.daily.map(day => day.temperatureMin));
});

const weeklyMax = computed(() => {
  if (!props.daily || props.daily.length === 0) return 0;
  return Math.max(...props.daily.map(day => day.temperatureMax));
});

/**
 * Calculates the percentage position of a temperature within the weekly range.
 * @param {number} temperatureValue - The temperature to position.
 * @returns {string} - The CSS percentage value (e.g., "25.5%").
 */
function getTempPosition(temperatureValue) {
  const temperatureRange = weeklyMax.value - weeklyMin.value;
  if (temperatureRange <= 0) return '50%';
  const positionPercentage = ((temperatureValue - weeklyMin.value) / temperatureRange) * 100;
  return `${Math.max(0, Math.min(100, positionPercentage))}%`;
}

watch(
    [activeCanvas, () => props.daily, () => settingsStore.hideEarlyMorning,
      () => settingsStore.temperatureUnits, () => settingsStore.theme],
    (_, __, onCleanup) => {
      const canvas = activeCanvas.value;
      const index = props.expandedDayIndex;
      const day = props.daily[index];
      if (!canvas || !day) return;

      const draw = () => drawTempChart(canvas, day, weeklyMin.value, weeklyMax.value, props.daily, index, {
        hideEarlyMorning: settingsStore.hideEarlyMorning,
        temperatureUnits: settingsStore.temperatureUnits,
      });
      draw();
      const observer = new ResizeObserver(draw);
      observer.observe(canvas);
      onCleanup(() => observer.disconnect());
    },
    {flush: 'post', immediate: true}
);

</script>

<style scoped>
.temp-bar-element-base {
  @apply absolute top-1/2 -translate-y-1/2;
}
</style>
